import { useUser } from "@clerk/tanstack-react-start";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import {
  Award,
  Clock,
  GraduationCap,
  Mail,
  ShieldAlert,
  Sparkles,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { FeatureTeaser } from "~/components/feature-teaser";
import { useAppForm } from "~/components/form/app-form";
import { FieldErrorsProvider } from "~/components/form/fields/field-errors";
import { Button } from "~/components/ui/button";
import {
  REFERRAL_SOURCES,
  coachApplicationSchema,
  submitCoachApplication,
} from "~/lib/functions/coach-application";
import { myOrganizationInviteQueryOptions } from "~/lib/functions/organization-invites";
import { recordIntentAnswerFn } from "~/lib/functions/user-intent";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";
import {
  inviteCredentialName,
  type OrganizationInvite,
} from "~/lib/shared/organization-invite";
import {
  POST_AUTH_COOKIE_NAMES,
  parseAcquisitionLaneCookie,
  readPostAuthCookie,
  setPostAuthCookies,
} from "~/lib/shared/post-auth-routing";

const becomeACoachSearchSchema = z.object({
  // Program/workshop entry marker — coach links promoted alongside a live
  // program carry `?src=program` so registration attributes to the program
  // lane instead of self-serve (R28).
  src: z.enum(["program"]).optional(),
});

export const Route = createFileRoute("/_user/become-a-coach")({
  staticData: { title: "Join as a Coach" },
  validateSearch: (search: Record<string, unknown>) =>
    becomeACoachSearchSchema.parse(search),
  beforeLoad: async ({ context }) => {
    // fetchQuery (not ensureQueryData) so a just-approved coach is redirected on
    // their next visit: ensureQueryData returns the sidebar-kept-alive cached
    // "pending" value without revalidating, whereas fetchQuery honours the
    // status query's staleTime (0) and re-reads approval fresh (issue #900).
    const status = await context.queryClient.fetchQuery(
      coachStatusQueryOptions(context.user.clerkUserId),
    );
    if (status?.accountStatus === "approved") {
      throw redirect({ to: "/my-coaching" });
    }
  },
  component: BecomeACoach,
});

function PendingTeaser() {
  return (
    <FeatureTeaser
      icon={Clock}
      headline="Your Application Is Under Review"
      description="Thanks for applying! Our team is reviewing your profile. We'll be in touch soon."
      features={[
        {
          icon: Mail,
          text: "You'll receive an email once your application is reviewed",
        },
        {
          icon: Star,
          text: "Most applications are reviewed within a few business days",
        },
        {
          icon: TrendingUp,
          text: "Once approved, you can start building your coaching profile",
        },
      ]}
    />
  );
}

function RevokedTeaser() {
  return (
    <FeatureTeaser
      icon={ShieldAlert}
      headline="Your Coach Access Has Been Revoked"
      description="Your coach access on Dream Coach Match has been revoked. If you believe this is a mistake, reach out to support@dreamcoachmatch.com for help."
      features={[]}
    />
  );
}

const REFERRAL_SOURCE_OPTIONS = REFERRAL_SOURCES.map((v) => ({
  value: v,
  label: v,
}));

const DIRECT_INVITATION =
  "From direct invitation (Email, DM)" satisfies (typeof REFERRAL_SOURCES)[number];

/**
 * The organization invite the coach followed: its credential is added as a
 * claim waiting for the organization unless she removes it here.
 */
function InviteNotice({
  invite,
  removed,
  onRemovedChange,
}: {
  invite: OrganizationInvite;
  removed: boolean;
  onRemovedChange: (removed: boolean) => void;
}): React.JSX.Element {
  const organization = invite.organization.name;
  const credential = inviteCredentialName(invite);
  return (
    <div className="rounded-lg border border-purple-100 bg-purple-50 p-3 text-sm text-purple-900">
      Invited by {organization}.{" "}
      {removed ? (
        <>
          {credential} won't be added to your qualifications.{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => onRemovedChange(false)}
          >
            Add it back
          </button>
        </>
      ) : (
        <>
          <span className="font-medium">{credential}</span> will be added to
          your qualifications and wait for {organization} to confirm it.{" "}
          <button
            type="button"
            className="underline underline-offset-2"
            onClick={() => onRemovedChange(true)}
          >
            Remove
          </button>
        </>
      )}
    </div>
  );
}

function ApplicationForm({
  lane,
  invite,
}: {
  lane: "self_serve" | "program" | undefined;
  invite: OrganizationInvite | null;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [inviteRemoved, setInviteRemoved] = useState(false);

  const form = useAppForm({
    defaultValues: {
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      linkedinProfile: "",
      website: "",
      referralSource: (invite ? DIRECT_INVITATION : "") as
        | (typeof REFERRAL_SOURCES)[number]
        | "",
      referredByCode: "",
      certificationDay: "",
    },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(value);
    },
  });

  const values = useStore(form.store, (s) => s.values);

  // Clerk resolves after the form is created — seed name fields once loaded
  // (mirrors the welcome wizard's nameForm.reset pattern). Freeze after the
  // first prefill via `didPrefill` so it never clobbers in-progress edits.
  const [didPrefill, setDidPrefill] = useState(false);
  useEffect(() => {
    if (!isLoaded || !user || didPrefill) return;
    setDidPrefill(true);
    form.reset({
      ...form.state.values,
      firstName: form.state.values.firstName || (user.firstName ?? ""),
      lastName: form.state.values.lastName || (user.lastName ?? ""),
    });
  }, [isLoaded, user, didPrefill, form]);

  const parsed = coachApplicationSchema.safeParse(values);
  const errorFor = (name: string): string | undefined => {
    if (!submitAttempted || parsed.success) return undefined;
    return parsed.error.issues.find((i) => i.path[0] === name)?.message;
  };

  const mutation = useMutation({
    mutationFn: ({ certificationDay, ...data }: typeof values) =>
      submitCoachApplication({
        data: coachApplicationSchema.parse({
          ...data,
          lane,
          organizationInvite: invite
            ? {
                addClaim: !inviteRemoved,
                certificationDay:
                  invite.program && !inviteRemoved && certificationDay
                    ? certificationDay
                    : null,
              }
            : undefined,
        }),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["coach-status"] });
      // Instant approval: the coach's home is immediately live — navigate
      // straight there instead of leaving them on the now-stale form (the
      // query invalidation above doesn't by itself re-run this route's
      // beforeLoad redirect). `/my-coaching` lets the index route pick
      // Welcome or the dashboard by flag, so this decision lives in one
      // place.
      if (result.instantlyApproved) {
        navigate({ to: "/my-coaching" });
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!parsed.success) return;
    void form.handleSubmit();
  };

  return (
    <FeatureTeaser
      icon={GraduationCap}
      headline="Join as a Coach"
      description="Join our platform and connect with clients who are looking for coaching. Share your expertise and grow your practice."
      features={[
        {
          icon: Users,
          text: "Get matched with clients looking for your expertise",
        },
        {
          icon: Award,
          text: "Build your reputation with reviews and ratings",
        },
        {
          icon: Sparkles,
          text: "Manage engagements, invoices, and conversations in one place",
        },
      ]}
    >
      <FieldErrorsProvider value={errorFor}>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <form.AppField name="firstName">
              {(f) => (
                <f.TextField
                  label="First Name"
                  placeholder="First name"
                  required
                />
              )}
            </form.AppField>
            <form.AppField name="lastName">
              {(f) => (
                <f.TextField
                  label="Last Name"
                  placeholder="Last name"
                  required
                />
              )}
            </form.AppField>
          </div>

          <form.AppField name="linkedinProfile">
            {(f) => (
              <f.TextField
                label="LinkedIn Profile"
                type="url"
                placeholder="https://linkedin.com/in/..."
              />
            )}
          </form.AppField>

          <form.AppField name="website">
            {(f) => (
              <f.TextField
                label="Website"
                type="url"
                placeholder="https://..."
              />
            )}
          </form.AppField>

          {invite && (
            <InviteNotice
              invite={invite}
              removed={inviteRemoved}
              onRemovedChange={setInviteRemoved}
            />
          )}

          <form.AppField name="referralSource">
            {(f) => (
              <f.SelectField
                label="How did you hear about Dream Coach Match?"
                placeholder="Select..."
                required
                options={REFERRAL_SOURCE_OPTIONS}
              />
            )}
          </form.AppField>

          {values.referralSource === "Referred by another coach" && (
            <form.AppField name="referredByCode">
              {(f) => (
                <f.TextField
                  label="Referral code"
                  placeholder="Enter their referral code"
                />
              )}
            </form.AppField>
          )}

          {invite?.program && !inviteRemoved && (
            <form.AppField name="certificationDay">
              {(f) => (
                <f.TextField
                  label="Certification date (optional)"
                  type="date"
                />
              )}
            </form.AppField>
          )}

          {mutation.isError && (
            <p className="text-sm text-red-600">
              Something went wrong. Please try again.
            </p>
          )}

          <Button type="submit" className="w-full" loading={mutation.isPending}>
            Submit Application
          </Button>
        </form>
      </FieldErrorsProvider>
    </FeatureTeaser>
  );
}

/**
 * Low-emphasis escape below the slim form (mirrors the `/welcome` fork's
 * "just exploring" treatment): an explicit correction for someone who landed
 * on the coach registration resume step by mistake. Overwrites `primaryIntent`
 * back to "client" (the only caller that needs `allowOverwrite` — the fork
 * itself never races this since it only renders for null intent).
 */
function LookingForCoachEscape() {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: () =>
      recordIntentAnswerFn({
        data: { answer: "client", allowOverwrite: true },
      }),
    onSuccess: () => navigate({ to: "/dashboard" }),
    onError: () => toast.error("Something went wrong. Please try again."),
  });

  return (
    <div className="mt-6 text-center">
      <button
        type="button"
        disabled={mutation.isPending}
        onClick={() => mutation.mutate()}
        className="text-sm text-gray-500 underline underline-offset-2 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Actually, I&apos;m looking for a coach
      </button>
    </div>
  );
}

function BecomeACoach() {
  const { user } = useUser();
  const search = useSearch({ from: "/_user/become-a-coach" });
  const { data: coachStatus, isLoading } = useQuery(
    coachStatusQueryOptions(user?.id),
  );
  const { data: invite = null, isLoading: inviteLoading } = useQuery({
    ...myOrganizationInviteQueryOptions(),
    enabled: !!user?.id,
  });

  // Known-intent entry point (R28): reaching this route implies coach intent
  // — a direct coach link or program invite (`?src=program`). Captured on
  // mount (client-only; `document.cookie` no-ops during SSR) so it lands
  // before the user ever authenticates.
  useEffect(() => {
    setPostAuthCookies({
      intent: "coach",
      lane: search.src === "program" ? "program" : undefined,
    });
  }, [search.src]);

  if (isLoading || inviteLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-40 w-full max-w-lg animate-pulse rounded-2xl border bg-neutral-100" />
      </div>
    );
  }

  const isCoach = coachStatus?.isCoach ?? false;
  const accountStatus = coachStatus?.accountStatus;
  const isRevoked = coachStatus?.isRevoked ?? false;

  // Revoked takes precedence over "under review" — a revoked coach must
  // never see the pending-review teaser (R24).
  if (isCoach && isRevoked) {
    return <RevokedTeaser />;
  }

  if (isCoach && accountStatus === "pending") {
    return <PendingTeaser />;
  }

  // Lane attribution (AE4): the route's own `?src=program` search param wins
  // — it's the freshest signal for this visit. Otherwise fall back to the
  // `dcm_lane` cookie captured on mount above (covers e.g. a return visit
  // after the cookie was set elsewhere in the funnel, without the query
  // string present on this exact page load).
  const lane =
    search.src === "program"
      ? "program"
      : (parseAcquisitionLaneCookie(
          readPostAuthCookie(POST_AUTH_COOKIE_NAMES.lane),
        ) ?? undefined);

  return (
    <div>
      <ApplicationForm lane={lane} invite={invite} />
      <LookingForCoachEscape />
    </div>
  );
}
